import { useRef, useEffect } from 'react';
import './Accounting.css';
import { useReactToPrint } from 'react-to-print';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { QRCodeCanvas } from 'qrcode.react';

const formatNumber = (number, locale) => {
  // Ensure the number is a properly parsed float
  const parsedNumber = parseFloat(number);
  if (isNaN(parsedNumber)) {
    return '0.00';
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(parsedNumber);
};

const formatDate = (dateString, locale) => {
  const date = new Date(dateString);
  return moment(date).format('DD MMM YY');
};

// Payment method transformation function
const transformPaymentMethod = (paymentMethod) => {
  const paymentMethods = {
    'bank_transfer': 'Bank Transfer',
    'credit_card': 'Credit Card',
    'cash': 'Cash',
    'debit_card': 'Debit Card',
    'online_payment': 'Online Payment',
    'cheque': 'Cheque',
    'mobile_payment': 'Mobile Payment'
  };
  
  return paymentMethods[paymentMethod] || paymentMethod || 'N/A';
};

// Generate QR code data from receipt number
const generateQRCodeData = (receiptNo) => {
 if (!receiptNo) return 'RECEIPT-000000';
 return receiptNo;
};

const ReceiptModal = ({ show, onClose, registration }) => {
  const { t, i18n } = useTranslation();
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  // This useEffect will help show a warning if inventory data is missing
  useEffect(() => {
    if (registration && !registration.inventory_items) {
    }
  }, [registration]);

  if (!show || !registration) return null;

  // Ensure all values are properly parsed as numbers
  const classPrice = parseFloat(registration.price || 0);
  const inventoryTotal = parseFloat(registration.inventory_price || 0);
  const totalBeforeDiscount = classPrice + inventoryTotal;
  const discountAmount = parseFloat(registration.discount || 0);
  const totalAfterDiscount = totalBeforeDiscount - discountAmount;
  const vatAmount = parseFloat(registration.vat || 0);
  const grandTotal = parseFloat(registration.final_price || 0) || (totalAfterDiscount + vatAmount);
  const payAmount = parseFloat(registration.pay_amount || 0);
  const remainingBalance = parseFloat(registration.remaining_balance || 0);

  // Generate QR code data
  const qrCodeData = generateQRCodeData(registration.receipt_no);

  return (
    <div className="invoice-modal-overlay" style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div className="invoice-modal" ref={componentRef} style={{ 
        backgroundColor: 'white',
        padding: '20px',
        width: '210mm',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative',
      }}>
        {/* Refund watermark if applicable */}
        {registration.confirmation_status === 'refund' && (
          <div className="refund-watermark" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-45deg)',
            fontSize: '72px',
            color: 'rgba(255, 0, 0, 0.2)',
            fontWeight: 'bold',
            zIndex: 0,
            pointerEvents: 'none'
          }}>
            {t('refund')}
          </div>
        )}
        
        <span className="modal-close-button" onClick={onClose} style={{
          position: 'absolute',
          top: '10px',
          right: '15px',
          fontSize: '24px',
          cursor: 'pointer',
          zIndex: 1
        }}>&times;</span>
        
        {/* Header section with logo and company info */}
        <div className="modal-header" style={{ 
          display: 'flex', 
          marginBottom: '20px',
          alignItems: 'flex-start'
        }}>
          {/* Left side - Logo */}
          <div className="company-logo" style={{ 
            width: '80px', 
            height: '80px',
            marginRight: '20px'
          }}>
            <img src="/images/plann_logo.png" alt="company logo" style={{ 
              width: '130%',
              height: 'auto'
            }} />
          </div>
          
          {/* Middle - Company info */}
          <div style={{ flex: 1, paddingTop: '5px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: 'bold', 
              marginBottom: '5px',
              marginTop: 0,
              color: '#000000',
              textAlign: 'left',
              marginLeft: '30px'
            }}>PLANN TECH Co.,Ltd.</h3>
            <p style={{ 
              fontSize: '13px', 
              margin: '2px 0',
              lineHeight: '1.3',
              color: '#000000',
              textAlign: 'left',
              marginLeft: '30px'
            }}>123 Sukhumvit Rd.,</p>
            <p style={{ 
              fontSize: '13px', 
              margin: '2px 0',
              lineHeight: '1.3',
              color: '#000000',
              textAlign: 'left',
              marginLeft: '30px'
            }}>Khlong Tan, Bangkok 10110</p>
            <p style={{ 
              fontSize: '13px', 
              margin: '2px 0',
              lineHeight: '1.3',
              color: '#000000',
              textAlign: 'left',
              marginLeft: '30px'
            }}>TAX ID 0105565096587</p>
            <p style={{ 
              fontSize: '13px', 
              margin: '2px 0',
              lineHeight: '1.3',
              color: '#000000',
              textAlign: 'left',
              marginLeft: '30px'
            }}>Tel: 065-2644595</p>
            <p style={{ 
              fontSize: '13px', 
              margin: '2px 0',
              lineHeight: '1.3',
              color: '#000000',
              textAlign: 'left',
              marginLeft: '30px'
            }}>Email: info@plann.tech</p>
          </div>
          
          {/* Right side - Receipt box */}
          <div className="invoice-box" style={{ 
            backgroundColor: 'rgb(240, 245, 255)', 
            padding: '15px', 
            color: '#000',
            borderRadius: '5px',
            width: '200px'
          }}>
            <h2 style={{ 
              color: 'rgb(70, 70, 255)', 
              fontSize: '24px', 
              margin: '0 0 15px 0',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              {t('receipt').toUpperCase()}
            </h2>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              fontSize: '13px', 
              margin: '5px 0' 
            }}>
              <span style={{ fontWeight: 'normal' }}>{t('No.')}:</span>
              <span>{registration.receipt_no || '-'}</span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              fontSize: '13px', 
              margin: '5px 0' 
            }}>
              <span style={{ fontWeight: 'normal' }}>{t('date')}:</span>
              <span>{formatDate(registration.timestamp || registration.date, i18n.language)}</span>
            </div>
          </div>
        </div>
        
        
        {/* Customer Information */}
        <div className="customer-info" style={{ marginBottom: '15px' }}>
          <h3 style={{ 
            fontSize: '14px', 
            fontWeight: 'bold', 
            marginBottom: '20px',
            color: '#000000',
          }}>{t('Customer Information')}</h3>
          <p style={{ fontSize: '13px', margin: '10px 0', color: '#000000' }}>
            <strong>{t('name')}:</strong> {registration.firstname} {registration.lastname || registration.student_name || t('na')}
          </p>
          <p style={{ fontSize: '13px', margin: '10px 0', color: '#000000' }}>
            <strong>{t('address')}:</strong> {registration.address || t('na')}
          </p>
          <p style={{ fontSize: '13px', margin: '10px 0', color: '#000000' }}>
            <strong>{t('phone')}:</strong> {registration.phone || t('na')}
          </p>
        </div>
        
        <hr style={{ 
          border: 'none', 
          height: '1px', 
          backgroundColor: 'rgb(220, 220, 240)', 
          margin: '15px 0'
        }} />
        
        {/* Receipt Table */}
        <div className="invoice-table" style={{ marginTop: '20px' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            fontSize: '12px'
          }}>
            <thead>
              <tr style={{ 
                backgroundColor: 'rgb(240, 240, 250)', 
                color: 'rgb(50, 50, 50)'
              }}>
                <th style={{ border: '1px solid #ddd', padding: '8px', width: '5%', textAlign: 'center' }}>#</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', width: '45%' }}>{t('description')}</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', width: '10%', textAlign: 'center' }}>{t('quantity')}</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', width: '10%', textAlign: 'center' }}>{t('unit')}</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', width: '15%', textAlign: 'right' }}>{t('unitPrice')}</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', width: '15%', textAlign: 'right' }}>{t('total')}</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ backgroundColor: 'rgb(248, 248, 255)' }}>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>1</td>
                <td style={{ border: '1px solid #ddd', padding: '8px' }}>{registration.class_code || t('class')}</td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>1</td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Course</td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{formatNumber(classPrice, i18n.language)}</td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{formatNumber(classPrice, i18n.language)}</td>
              </tr>
              
              {/* Show inventory items if present */}
              {registration.inventory_items && registration.inventory_items.length > 0 ? (
                registration.inventory_items.map((item, index) => {
                  // Parse the item if it's a string (from JSON_AGG in PostgreSQL)
                  let parsedItem;
                  try {
                    parsedItem = typeof item === 'string' ? JSON.parse(item) : item;
                  } catch (e) {
                    console.error("Error parsing inventory item:", e);
                    parsedItem = { label: "Error parsing item", price_per_unit: 0 };
                  }
                  
                  return (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'white' : 'rgb(248, 248, 255)' }}>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{index + 2}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{parsedItem.label}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>1</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Book</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{formatNumber(parsedItem.price_per_unit, i18n.language)}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{formatNumber(parsedItem.price_per_unit, i18n.language)}</td>
                    </tr>
                  );
                })
              ) : (
                // Show note about missing inventory items - only when debugging
                registration.inventory_price > 0 && (
                  <tr style={{ backgroundColor: 'white' }}>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>2</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Books/Materials</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>1</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Item</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{formatNumber(registration.inventory_price || 0, i18n.language)}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{formatNumber(registration.inventory_price || 0, i18n.language)}</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
        
        {/* Summary Information */}
        <div className="invoice-summary" style={{ 
           fontSize: 12,
                  border: '1px solid #d9d9d9',
                  padding: 5,
                  backgroundColor: '#fafafa',
                  borderRadius: 6,
                  width: 300,
                  marginLeft: 'auto' 
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0' }}>
            <span>{t('subtotal')}:</span>
            <span>{formatNumber(totalBeforeDiscount, i18n.language)}</span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0' }}>
            <span>{t('discount')}:</span>
            <span>{formatNumber(discountAmount, i18n.language)}</span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0' }}>
            <span>{t('Total After Discount')}:</span>
            <span>{formatNumber(totalAfterDiscount, i18n.language)}</span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0' }}>
            <span>{t('vat', { percentage: 7 })}:</span>
            <span>{formatNumber(vatAmount, i18n.language)}</span>
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            margin: '6px 0',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            <span>{t('grandTotal')}:</span>
            <span>{formatNumber(grandTotal, i18n.language)}</span>
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            margin: '6px 0',
            color: 'green',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            <span>{t('Paid Amount')}:</span>
            <span>{formatNumber(payAmount, i18n.language)}</span>
          </div>
          
          {/* Only show remaining balance if needed */}
          {remainingBalance > 0 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              margin: '6px 0',
              color: 'red',
              fontWeight: 'bold',
              fontSize: '14px'
            }}>
              <span>{t('Remaining Balance')}:</span>
              <span>({formatNumber(remainingBalance, i18n.language)})</span>
            </div>
          )}
        </div>

        {/* Updated Payment Information */}
        <div className="payment-info" style={{ marginTop: '40px' }}>
          <div className="payment-details" style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            marginBottom: '15px'
          }}>
            <div style={{ width: '48%' }}>
              <div style={{ 
                border: '1px solid #ddd', 
                padding: '15px', 
                borderRadius: '8px',
                backgroundColor: '#f9f9f9',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <h4 style={{ 
                  fontSize: '14px', 
                  fontWeight: 'bold', 
                  marginBottom: '10px',
                  color: '#333',
                  borderBottom: '1px solid #eee',
                  paddingBottom: '5px'
                }}>{t('Payment Information')}</h4>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>{t('Method')}:</span>
                  <p style={{ fontSize: '13px', margin: '2px 0 0 0', color: '#000', fontWeight: '500' }}>
                    {transformPaymentMethod(registration.payment_method)}
                  </p>
                </div>
                <div style={{ 
                  marginTop: '12px',
                  padding: '6px 10px',
                  backgroundColor: '#d4edda',
                  borderRadius: '4px',
                  border: '1px solid #c3e6cb'
                }}>
                  <span style={{ 
                    fontSize: '12px', 
                    fontWeight: 'bold', 
                    color: '#155724' 
                  }}>
                    {t('Payment Status')}: {t('Confirmed')}
                  </span>
                </div>
              </div>
            </div>
            
            <div style={{ width: '48%' }}>
              <div style={{ 
                border: '1px solid #ddd', 
                padding: '15px', 
                borderRadius: '8px',
                backgroundColor: '#f9f9f9',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <h4 style={{ 
                  fontSize: '14px', 
                  fontWeight: 'bold', 
                  marginBottom: '10px',
                  color: '#333',
                  borderBottom: '1px solid #eee',
                  paddingBottom: '5px'
                }}>{t('Transaction Details')}</h4>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>{t('Invoice No')}:</span>
                  <p style={{ fontSize: '12px', margin: '2px 0 0 0', color: '#000' }}>
                    {registration.invoice_no || t('na')}
                  </p>
                </div>
                <div style={{ 
                  marginTop: '12px',
                  padding: '6px 10px',
                  backgroundColor: '#e7f3ff',
                  borderRadius: '4px',
                  border: '1px solid #b6d7ff'
                }}>
                  <span style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>{t('Amount Paid')}: {formatNumber(payAmount, i18n.language)}</span>

                </div>
              </div>
            </div>
          </div>
        </div>

        {/* QR Code, Signature, and Print Info Footer */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-end',
          marginTop: '60px',
          marginBottom: '20px',
          padding: '20px 0',
          borderTop: '1px solid #eee'
        }}>
          {/* QR Code Section */}
          <div style={{ 
            width: '25%', 
            textAlign: 'center'
          }}>
            <QRCodeCanvas
              value={qrCodeData}
              size={60}
            />
            <div style={{ 
              fontSize: '10px', 
              marginTop: '8px', 
              color: '#666',
              fontFamily: 'monospace',
              wordBreak: 'break-all'
            }}>
              PLNN-RECEIPT
            </div>
          </div>
          
          {/* Signature Section */}
          <div style={{ 
            width: '50%', 
            textAlign: 'center',
            padding: '0 20px'
          }}>
            {registration.signature ? (
              <div>
                <img 
                  src={registration.signature} 
                  alt="Signature" 
                  style={{ 
                    maxWidth: '200px', 
                    maxHeight: '60px', 
                    marginBottom: '5px' 
                  }} 
                />
                <div style={{ 
                  fontSize: '12px',
                  color: '#333',
                  fontWeight: 'bold'
                }}>
                  {t('Authorized Signature')}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ 
                  fontSize: '12px', 
                  fontWeight: 'bold', 
                  marginBottom: '80px',
                  color: '#333'
                }}>
                  Authorized by
                </div>
                <div style={{ 
                  borderTop: '1px solid #333', 
                  width: '300px', 
                  margin: '0 auto',
                  paddingTop: '8px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  {registration.created_by_firstname && registration.created_by_lastname ? (
                    <div style={{ fontWeight: '500' }}>
                      {registration.created_by_firstname} {registration.created_by_lastname}
                    </div>
                  ) : (
                    <div style={{ color: '#999' }}>
                     
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Print Info Section */}
          <div style={{ 
            width: '25%', 
            textAlign: 'right', 
            fontSize: '11px', 
            color: '#666'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              Printed On:
            </div>
            <div style={{ marginBottom: '2px' }}>
              {moment().format('DD MMM YY')}
            </div>
            <div>
              {moment().format('HH:mm:ss')}
            </div>
          </div>
        </div>
        
        {/* Note */}
        <div className="invoice-note" style={{ 
          textAlign: 'center', 
          marginTop: '10px',
          marginBottom: '15px'
        }}>
          <p style={{ fontSize: '9px', color: '#888' }}>
            {t('Note: We appreciate your business. Please note that all transactions are final, and refunds cannot be processed.')}
          </p>
        </div>
        
        {/* Print Button - Only visible when displaying the modal, not when printing */}
        <div className="invoice-actions"
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '15px',
            }}>
          <button 
            onClick={handlePrint}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4646ff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {t('printReceipt')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;